// FILE: src/layouts/PublicLayout.jsx
// Shell for pages used before sign-in (sign in, registration, password
// reset, system requirements, error pages). Two columns on wide screens:
// a brand panel and the page's card; one column on phones. Landmarks,
// skip link and the light/dark switch as in the signed-in shell.
import { Box, Container, Link, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Link as RouterLink } from "react-router-dom";
import Wordmark from "../components/brand/Wordmark";
import ThemeToggle from "../components/shell/ThemeToggle";
import { SkipLink } from "./MainLayout";
import NewVersionBanner from "../components/shell/NewVersionBanner";

const POINTS = [
  "Online in-class lessons for new Ontario drivers, no licence needed to start",
  "Learn at your own pace, up to 5 hours a day, with breaks built in",
  "Real people to help: technical, service and course-content support"
];

function BrandPanel() {
  return (
    <Box
      sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        p: 6,
        color: "#FFFFFF",
        bgcolor: "#1E5B3A",
        backgroundImage:
          "radial-gradient(circle at 85% 10%, rgba(255,255,255,0.10) 0, rgba(255,255,255,0) 40%), linear-gradient(160deg, #1E5B3A 0%, #174A2F 100%)",
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Decorative road line */}
      <Box
        component="svg"
        viewBox="0 0 400 600"
        aria-hidden="true"
        focusable="false"
        sx={{ position: "absolute", right: -80, bottom: -40, width: 420, opacity: 0.16 }}
      >
        <path d="M60 600 C 160 420, 120 300, 260 200 S 380 40, 360 0" fill="none" stroke="#fff" strokeWidth="56" strokeLinecap="round" />
        <path d="M60 600 C 160 420, 120 300, 260 200 S 380 40, 360 0" fill="none" stroke="#1E5B3A" strokeWidth="4" strokeDasharray="22 22" strokeLinecap="round" />
      </Box>
      <Box sx={{ position: "relative" }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5 }}>
          <Box component="svg" viewBox="0 0 64 64" aria-hidden="true" focusable="false" sx={{ width: 40, height: 40 }}>
            <rect width="64" height="64" rx="14" fill="#FFFFFF" />
            <path d="M18 19h28L19 45h28" fill="none" stroke="#1E5B3A" strokeWidth="9" strokeLinejoin="round" strokeLinecap="round" />
            <path d="M18 19h28L19 45h28" fill="none" stroke="#FFFFFF" strokeWidth="1.6" strokeDasharray="3.5 4" strokeLinecap="round" />
          </Box>
          <Typography component="span" sx={{ fontWeight: 750, fontSize: "1.375rem", letterSpacing: "-0.01em" }}>
            Zun Drivers
          </Typography>
        </Box>
      </Box>
      <Box sx={{ position: "relative", maxWidth: 440 }}>
        <Typography component="p" sx={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em", mb: 3 }}>
          Beginner Driver Education, online.
        </Typography>
        <Stack component="ul" spacing={2} sx={{ listStyle: "none", p: 0, m: 0 }}>
          {POINTS.map((p) => (
            <Stack key={p} component="li" direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
              <CheckCircleOutlineIcon aria-hidden sx={{ mt: "2px" }} />
              <Typography sx={{ fontSize: "1.0625rem" }}>{p}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
      <Typography sx={{ position: "relative", opacity: 0.9 }}>© {new Date().getFullYear()} Zun Drivers</Typography>
    </Box>
  );
}

export default function PublicLayout({ children, maxWidth = 460, wide = false }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: wide ? "1fr" : { xs: "1fr", md: "minmax(380px, 5fr) 7fr" },
        bgcolor: "background.default"
      }}
    >
      <SkipLink />
      {!wide && <BrandPanel />}
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <NewVersionBanner />
        <Box
          component="header"
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: { xs: 2, sm: 4 }, py: 2 }}
        >
          <Box sx={{ display: wide ? "block" : { xs: "block", md: "none" } }}>
            <Link component={RouterLink} to="/login" underline="none" aria-label="Zun Drivers, sign in page" sx={{ display: "block" }}>
              <Wordmark size={30} />
            </Link>
          </Box>
          <Box sx={{ ml: "auto" }}>
            <ThemeToggle />
          </Box>
        </Box>
        <Container
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: wide ? "flex-start" : "center",
            maxWidth: `${wide ? 880 : maxWidth}px !important`,
            px: { xs: 2, sm: 3 },
            py: { xs: 2, md: 4 },
            "&:focus": { outline: "none" }
          }}
        >
          {children}
        </Container>
        <Box component="footer" sx={{ px: { xs: 2, sm: 4 }, py: 3, color: "text.secondary" }}>
          <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", rowGap: 1, justifyContent: "center" }}>
            <Link component={RouterLink} to="/help" color="text.secondary">
              Help centre
            </Link>
            <Link component={RouterLink} to="/contact" color="text.secondary">
              Contact us
            </Link>
            <Link component={RouterLink} to="/system-requirements" color="text.secondary">
              System requirements
            </Link>
            <Link component={RouterLink} to="/accessibility" color="text.secondary">
              Accessibility
            </Link>
            <Link component={RouterLink} to="/privacy" color="text.secondary">
              Privacy
            </Link>
            <Link component={RouterLink} to="/login" color="text.secondary">
              Sign in
            </Link>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
